'use client';

interface SegmentedToggleProps {
  leftLabel: string;
  rightLabel: string;
  /** True when the LEFT option is the selected one. */
  leftSelected: boolean;
  onSelectLeft: () => void;
  onSelectRight: () => void;
  /**
   * Per-instance test id root. Several of these render together on the Create
   * Group popup, so each needs its own stable handle — tests must never
   * disambiguate sibling controls by array index.
   */
  testIdPrefix: string;
  disabled?: boolean;
}

/**
 * MWBI-890 — the two-option segmented pill used by the Create Group popup.
 *
 * The Figma panel for the Create popup keeps the app's existing segmented
 * button-pair look (the same control the Accepting Members / Approval Mode
 * settings already used), NOT the green sliding switch. The green switch
 * ([[GroupSettingToggle]]) is the Edit Group form's control per its own panel.
 * Both screens set the same underlying flags; only the presentation differs,
 * matching the two design panels.
 *
 * Selection is exposed to assistive tech via `aria-pressed` on each half, so
 * which option is active is not conveyed by background colour alone.
 */
export function SegmentedToggle({
  leftLabel,
  rightLabel,
  leftSelected,
  onSelectLeft,
  onSelectRight,
  testIdPrefix,
  disabled = false,
}: SegmentedToggleProps) {
  const optionClass = (selected: boolean) =>
    `rounded-md px-4 py-2.5 text-sm font-medium transition focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed ${
      selected
        ? 'bg-background text-foreground shadow-sm'
        : 'text-foreground-muted hover:text-foreground'
    }`;

  return (
    <div
      className="rounded-lg border border-border bg-background-secondary p-1"
      data-testid={`${testIdPrefix}-segmented`}
    >
      <div className="grid grid-cols-2 gap-1">
        <button
          type="button"
          aria-pressed={leftSelected}
          onClick={onSelectLeft}
          disabled={disabled}
          data-testid={`${testIdPrefix}-left-btn`}
          className={optionClass(leftSelected)}
        >
          {leftLabel}
        </button>
        <button
          type="button"
          aria-pressed={!leftSelected}
          onClick={onSelectRight}
          disabled={disabled}
          data-testid={`${testIdPrefix}-right-btn`}
          className={optionClass(!leftSelected)}
        >
          {rightLabel}
        </button>
      </div>
    </div>
  );
}
